// src/pages/boom-sprayer/summary-boom/SummaryBoom.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Stack,
    Chip,
    Menu,
    MenuItem,
    Dialog,
    Slide,
    IconButton,
} from "@mui/material";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { BarChart } from "@mui/x-charts/BarChart";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Import CustomTableContainer
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Import DetailSummaryBoom component
import DetailSummaryBoom from "./DetailSummaryBoom";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Daftar aktivitas
const aktivitasList = [
    "Pre Planting",
    "Herb.post plant./emerg. mek.",
    "Herbisida booster Mekanis",
    "Pupuk spray mek.  <ppk>",
    "Forcing 1 (satu) mek.",
    "Forcing 2 (dua) mek.",
    "Insektisida I Setelah Forcing",
    "Ripening",
    "Pestisida Spray",
    "Siram Buah"
];

// Fungsi untuk mendapatkan daftar week (1-53, karena tahun bisa memiliki 53 weeks)
const getWeekList = () => {
    return Array.from({ length: 53 }, (_, i) => i + 1);
};

// Fungsi untuk mendapatkan tanggal Senin pertama dalam ISO Week untuk tahun tertentu
const getFirstMondayOfYear = (year) => {
    // Tanggal 4 Januari selalu berada di Week 1 (ISO standard)
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay(); // 0=Minggu, 1=Senin, ..., 6=Sabtu
    
    // Hitung offset ke Senin
    const offsetToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    
    const firstMonday = new Date(jan4);
    firstMonday.setDate(jan4.getDate() + offsetToMonday);
    return firstMonday;
};

// Fungsi untuk mendapatkan tanggal-tanggal dalam suatu week (Senin-Minggu) sesuai ISO Week
const getDatesForWeek = (year, week) => {
    const dates = [];
    
    const firstMonday = getFirstMondayOfYear(year);
    
    // Hitung tanggal Senin untuk week yang diminta
    const mondayOfWeek = new Date(firstMonday);
    mondayOfWeek.setDate(firstMonday.getDate() + (week - 1) * 7);
    
    // Generate tanggal dari Senin sampai Minggu (7 hari)
    for (let i = 0; i < 7; i++) {
        const currentDate = new Date(mondayOfWeek);
        currentDate.setDate(mondayOfWeek.getDate() + i);
        dates.push(currentDate);
    }
    
    return dates;
};

// Fungsi untuk mendapatkan tanggal-tanggal dalam suatu week, tetapi hanya yang termasuk dalam tahun yang dipilih
const getWeekDateRange = (year, week) => {
    const allWeekDates = getDatesForWeek(year, week);
    
    // Filter hanya tanggal yang ada di tahun yang dipilih
    const datesInSelectedYear = allWeekDates.filter(date => date.getFullYear() === year);
    
    return datesInSelectedYear;
};

// Format tanggal menjadi "DD/MM/YYYY"
const formatDate = (date) => {
    if (!date) return "-";
    try {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch {
        return "-";
    }
};

// Format tanggal menjadi "DD-MM-YYYY" (untuk matching dengan Rencana Bulan Ini)
const formatDateToDDMMYYYY = (date) => {
    if (!date) return "";
    try {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    } catch {
        return "";
    }
};

// Format tanggal menjadi "DD/MM/YYYY" dari string "DD-MM-YYYY"
const formatDateFromDDMMYYYYToDDMMYYYY = (dateStr) => {
    if (!dateStr || dateStr === '-' || dateStr === '') return "";
    
    const normalizedStr = dateStr.toString().trim();
    
    // Format DD-MM-YYYY (format dari rencana_bulan_ini)
    const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
    const dashMatch = normalizedStr.match(dashPattern);
    if (dashMatch) {
        const [_, day, month, year] = dashMatch;
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
    
    // Format DD/MM/YYYY (sudah sesuai)
    const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const slashMatch = normalizedStr.match(slashPattern);
    if (slashMatch) {
        const [_, day, month, year] = slashMatch;
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
    
    return "";
};

// Generate daftar tahun
const getYearList = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => currentYear - 5 + i).filter(year => year >= 2020);
};

// Helper: Parse tanggal dari format DD-MM-YYYY ke Date object
const parseDateFromDDMMYYYY = (dateStr) => {
    if (!dateStr || dateStr === '-' || dateStr === '') return null;
    
    const normalizedStr = dateStr.toString().trim();
    
    // Format DD-MM-YYYY (format dari rencana_bulan_ini)
    const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
    const dashMatch = normalizedStr.match(dashPattern);
    if (dashMatch) {
        const [_, day, month, year] = dashMatch;
        return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    }
    
    // Format DD/MM/YYYY (fallback)
    const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const slashMatch = normalizedStr.match(slashPattern);
    if (slashMatch) {
        const [_, day, month, year] = slashMatch;
        return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    }
    
    return null;
};

// Helper: Normalisasi string untuk matching (case insensitive, trim whitespace)
const normalizeString = (str) => {
    if (!str) return '';
    return str.toString().trim().toLowerCase();
};

// Fungsi untuk mendapatkan week ISO dari tanggal tertentu
const getISOWeek = (date) => {
    const target = new Date(date.valueOf());
    const dayNumber = (date.getDay() + 6) % 7; // Adjust so Monday is 0, Sunday is 6
    target.setDate(target.getDate() - dayNumber + 3); // Set to Thursday of the week
    const firstThursday = target.valueOf();
    target.setMonth(0, 1); // Set to January 1
    if (target.getDay() !== 4) { // If Jan 1 is not Thursday
        target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7); // Set to first Thursday
    }
    const weekNumber = 1 + Math.ceil((firstThursday - target) / 604800000); // 604800000 = 7 * 24 * 60 * 60 * 1000
    return weekNumber;
};

// Slide transition untuk dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Helper function untuk memastikan nilai adalah number yang valid
const ensureNumber = (value) => {
    if (value === null || value === undefined || value === "-") return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
};

function SummaryBoom() {
    const [dataList, setDataList] = useState([]);
    const [realisasiDataList, setRealisasiDataList] = useState([]); // Data untuk tab Realisasi
    const [detailData, setDetailData] = useState([]);
    const [realisasiDetailData, setRealisasiDetailData] = useState([]); // Detail data untuk tab Realisasi
    const [chartData, setChartData] = useState({ 
        labels: [], 
        rencanaData: [], 
        realisasiData: [],
        selisihData: [],
        persentaseData: []
    }); // Data untuk chart
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);
    
    const [yearAnchorEl, setYearAnchorEl] = useState(null);
    const [weekAnchorEl, setWeekAnchorEl] = useState(null);
    
    const [boomData, setBoomData] = useState([]);
    const [lokasiData, setLokasiData] = useState([]);
    
    const yearList = getYearList();
    const weekList = getWeekList();
    
    const [weekDates, setWeekDates] = useState([]);
    
    // State untuk dialog detail
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);

    // Fetch data dari Firebase
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                
                // Fetch boom spraying data
                const boomRef = ref(database, "db_boom_spraying/tb_rencana_boom_spraying");
                const boomSnapshot = await onValuePromise(boomRef);
                const boomRawData = boomSnapshot.val();
                
                let boomArray = [];
                if (boomRawData) {
                    boomArray = Object.keys(boomRawData).map(key => ({
                        firebaseKey: key,
                        ...boomRawData[key]
                    }));
                }
                setBoomData(boomArray);
                
                // Fetch lokasi data
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiRawData = lokasiSnapshot.val();
                
                let lokasiArray = [];
                if (lokasiRawData) {
                    lokasiArray = Object.keys(lokasiRawData).map(key => ({
                        firebaseKey: key,
                        ...lokasiRawData[key]
                    }));
                }
                setLokasiData(lokasiArray);
                
            } catch (err) {
                console.error("❌ Error fetching data:", err);
                setError("Gagal memuat data dari database");
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, []);

    // Inisialisasi tahun dan week default berdasarkan tanggal saat ini
    useEffect(() => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentWeek = getISOWeek(today);
        
        console.log("Tanggal saat ini:", formatDate(today));
        console.log("Tahun saat ini:", currentYear);
        console.log("Week ISO saat ini:", currentWeek);
        
        // Set default: Tahun dan Week saat ini
        setSelectedYear(currentYear);
        setSelectedWeek(currentWeek);
        const dates = getWeekDateRange(currentYear, currentWeek);
        setWeekDates(dates);
        
        // Debug log untuk melihat tanggal-tanggal Week saat ini
        console.log(`Week ${currentWeek} ${currentYear} dates:`, dates.map(d => formatDate(d)));
        console.log(`Jumlah hari di Week ${currentWeek} ${currentYear}:`, dates.length);
    }, []);

    // Fungsi untuk generate data rencana (tab Rencana)
    const generateRencanaData = (boomData, lokasiData, weekDateRange, selectedYear, selectedWeek) => {
        const summaryData = [];
        const detailDataPerAktivitas = [];
        
        // Buat mapping untuk setiap tanggal di week
        const dateMapping = {};
        weekDateRange.forEach((date, index) => {
            const dateDDMMYYYY = formatDateToDDMMYYYY(date);
            const dateDDMMYYYYSlash = formatDate(date);
            dateMapping[dateDDMMYYYY] = index;
            dateMapping[dateDDMMYYYYSlash] = index;
        });
        
        // Buat lookup untuk lokasi data berdasarkan id_lokasi
        const lokasiLookup = {};
        lokasiData.forEach(lokasi => {
            if (lokasi.id_lokasi) {
                lokasiLookup[lokasi.id_lokasi] = lokasi;
            }
        });
        
        // Process each aktivitas untuk RENCANA
        aktivitasList.forEach((aktivitas, index) => {
            // Inisialisasi array luas per tanggal (sesuai jumlah tanggal di week)
            const luasPerTanggal = new Array(weekDateRange.length).fill(0);
            let totalLuas = 0;
            
            // Data detail untuk aktivitas ini
            const aktivitasDetails = [];
            
            // Filter data boom untuk aktivitas ini
            const filteredBoomData = boomData.filter(boomItem => 
                normalizeString(boomItem.aktifitas) === normalizeString(aktivitas)
            );
            
            console.log(`RENCANA - Aktivitas "${aktivitas}": ${filteredBoomData.length} data ditemukan`);
            
            // Process data boom untuk aktivitas ini
            filteredBoomData.forEach((boomItem, boomIndex) => {
                // Gunakan rencana_bulan_ini untuk tab Rencana
                const rencanaBulanIni = boomItem.rencana_bulan_ini || '-';
                
                // Cari data lokasi berdasarkan id_lokasi
                const lokasiItem = lokasiLookup[boomItem.id_lokasi];
                
                // Gunakan luasan langsung dari boomData
                const luasan = boomItem.luasan || '-';
                
                // Tambahkan ke detail data
                const detailItem = {
                    id_lokasi: boomItem.id_lokasi || '-',
                    nama_lokasi: lokasiItem?.lokasi || '-',
                    luas_aktif: luasan,
                    real_bulan_lalu: boomItem.real_bulan_lalu || '-',
                    interval_rencana: boomItem.interval_rencana || '-',
                    rencana_bulan_ini: rencanaBulanIni,
                    real_bulan_ini: boomItem.real_bulan_ini || '-', // Tambahkan untuk perbandingan
                    tahun_rencana: '-',
                    matches_target_date: false,
                    matches_target_year: false
                };
                
                if (rencanaBulanIni && rencanaBulanIni !== '-') {
                    const rencanaDate = parseDateFromDDMMYYYY(rencanaBulanIni);
                    
                    if (rencanaDate) {
                        const rencanaYear = rencanaDate.getFullYear();
                        detailItem.tahun_rencana = rencanaYear;
                        detailItem.matches_target_year = rencanaYear === selectedYear;
                        
                        if (rencanaYear === selectedYear) {
                            // Cek dalam berbagai format
                            const formattedDateDDMMYYYY = formatDateToDDMMYYYY(rencanaDate);
                            const formattedDateSlash = formatDate(rencanaDate);
                            const formattedDateFromData = formatDateFromDDMMYYYYToDDMMYYYY(rencanaBulanIni);
                            
                            if (dateMapping.hasOwnProperty(formattedDateDDMMYYYY) || 
                                dateMapping.hasOwnProperty(formattedDateSlash) ||
                                dateMapping.hasOwnProperty(formattedDateFromData)) {
                                
                                const dateKey = dateMapping.hasOwnProperty(formattedDateDDMMYYYY) ? formattedDateDDMMYYYY :
                                               dateMapping.hasOwnProperty(formattedDateSlash) ? formattedDateSlash :
                                               formattedDateFromData;
                                
                                const dateIndex = dateMapping[dateKey];
                                detailItem.matches_target_date = true;
                                
                                // Gunakan luasan langsung dari boomData untuk perhitungan
                                if (luasan && luasan !== '-') {
                                    const luasParsed = parseFloat(luasan);
                                    
                                    if (!isNaN(luasParsed) && luasParsed > 0) {
                                        luasPerTanggal[dateIndex] += luasParsed;
                                        totalLuas += luasParsed;
                                        console.log(`RENCANA - ✅ MATCH! Tanggal: ${formattedDateDDMMYYYY}, Luasan: ${luasParsed}, Index: ${dateIndex}`);
                                    }
                                }
                            }
                        }
                    }
                }
                
                aktivitasDetails.push(detailItem);
            });
            
            // Simpan detail data untuk aktivitas ini
            detailDataPerAktivitas.push({
                aktivitas: aktivitas,
                jumlah_lokasi: aktivitasDetails.length,
                details: aktivitasDetails
            });
            
            // Create row untuk summary
            const dataRow = {
                no: index + 1,
                aktivitas: aktivitas,
                total: totalLuas > 0 ? totalLuas.toFixed(2) : "-",
            };
            
            // Tambahkan kolom untuk setiap tanggal
            for (let i = 0; i < weekDateRange.length; i++) {
                dataRow[`day_${i}`] = luasPerTanggal[i] > 0 ? luasPerTanggal[i].toFixed(2) : "-";
            }
            
            summaryData.push(dataRow);
        });
        
        return { summaryData, detailDataPerAktivitas };
    };

    // Fungsi untuk generate data realisasi (tab Realisasi)
    const generateRealisasiData = (boomData, lokasiData, weekDateRange, selectedYear, selectedWeek) => {
        const realisasiSummaryData = [];
        const realisasiDetailDataPerAktivitas = [];
        
        // Buat mapping untuk setiap tanggal di week
        const dateMapping = {};
        weekDateRange.forEach((date, index) => {
            const dateDDMMYYYY = formatDateToDDMMYYYY(date);
            const dateDDMMYYYYSlash = formatDate(date);
            dateMapping[dateDDMMYYYY] = index;
            dateMapping[dateDDMMYYYYSlash] = index;
        });
        
        // Buat lookup untuk lokasi data berdasarkan id_lokasi
        const lokasiLookup = {};
        lokasiData.forEach(lokasi => {
            if (lokasi.id_lokasi) {
                lokasiLookup[lokasi.id_lokasi] = lokasi;
            }
        });
        
        // Process each aktivitas untuk REALISASI
        aktivitasList.forEach((aktivitas, index) => {
            // Inisialisasi array luas per tanggal (sesuai jumlah tanggal di week)
            const luasPerTanggal = new Array(weekDateRange.length).fill(0);
            let totalLuas = 0;
            
            // Data detail untuk aktivitas ini (REALISASI)
            const aktivitasDetails = [];
            
            // Filter data boom untuk aktivitas ini
            const filteredBoomData = boomData.filter(boomItem => 
                normalizeString(boomItem.aktifitas) === normalizeString(aktivitas)
            );
            
            console.log(`REALISASI - Aktivitas "${aktivitas}": ${filteredBoomData.length} data ditemukan`);
            
            // Process data boom untuk aktivitas ini
            filteredBoomData.forEach((boomItem) => {
                // PERUBAHAN: Gunakan real_bulan_ini untuk tab Realisasi
                const realBulanIni = boomItem.real_bulan_ini || '-';
                
                // Cari data lokasi berdasarkan id_lokasi
                const lokasiItem = lokasiLookup[boomItem.id_lokasi];
                
                // Gunakan luasan langsung dari boomData
                const luasan = boomItem.luasan || '-';
                
                // Tambahkan ke detail data REALISASI
                const detailItem = {
                    id_lokasi: boomItem.id_lokasi || '-',
                    nama_lokasi: lokasiItem?.lokasi || '-',
                    luas_aktif: luasan,
                    real_bulan_lalu: boomItem.real_bulan_lalu || '-',
                    interval_rencana: boomItem.interval_rencana || '-',
                    rencana_bulan_ini: boomItem.rencana_bulan_ini || '-', // Tetap ditampilkan untuk referensi
                    real_bulan_ini: realBulanIni, // Kolom utama untuk tab Realisasi
                    tahun_realisasi: '-',
                    matches_target_date: false,
                    matches_target_year: false
                };
                
                if (realBulanIni && realBulanIni !== '-') {
                    const realDate = parseDateFromDDMMYYYY(realBulanIni);
                    
                    if (realDate) {
                        const realYear = realDate.getFullYear();
                        detailItem.tahun_realisasi = realYear;
                        detailItem.matches_target_year = realYear === selectedYear;
                        
                        if (realYear === selectedYear) {
                            // Cek dalam berbagai format
                            const formattedDateDDMMYYYY = formatDateToDDMMYYYY(realDate);
                            const formattedDateSlash = formatDate(realDate);
                            const formattedDateFromData = formatDateFromDDMMYYYYToDDMMYYYY(realBulanIni);
                            
                            if (dateMapping.hasOwnProperty(formattedDateDDMMYYYY) || 
                                dateMapping.hasOwnProperty(formattedDateSlash) ||
                                dateMapping.hasOwnProperty(formattedDateFromData)) {
                                
                                const dateKey = dateMapping.hasOwnProperty(formattedDateDDMMYYYY) ? formattedDateDDMMYYYY :
                                               dateMapping.hasOwnProperty(formattedDateSlash) ? formattedDateSlash :
                                               formattedDateFromData;
                                
                                const dateIndex = dateMapping[dateKey];
                                detailItem.matches_target_date = true;
                                
                                // Gunakan luasan langsung dari boomData untuk perhitungan
                                if (luasan && luasan !== '-') {
                                    const luasParsed = parseFloat(luasan);
                                    
                                    if (!isNaN(luasParsed) && luasParsed > 0) {
                                        luasPerTanggal[dateIndex] += luasParsed;
                                        totalLuas += luasParsed;
                                        console.log(`REALISASI - ✅ MATCH! Tanggal: ${formattedDateDDMMYYYY}, Luasan: ${luasParsed}, Index: ${dateIndex}`);
                                    }
                                }
                            }
                        }
                    }
                }
                
                aktivitasDetails.push(detailItem);
            });
            
            // Simpan detail data REALISASI untuk aktivitas ini
            realisasiDetailDataPerAktivitas.push({
                aktivitas: aktivitas,
                jumlah_lokasi: aktivitasDetails.length,
                details: aktivitasDetails
            });
            
            // Create row untuk summary realisasi
            const dataRow = {
                no: index + 1,
                aktivitas: aktivitas,
                total: totalLuas > 0 ? totalLuas.toFixed(2) : "-",
            };
            
            // Tambahkan kolom untuk setiap tanggal
            for (let i = 0; i < weekDateRange.length; i++) {
                dataRow[`day_${i}`] = luasPerTanggal[i] > 0 ? luasPerTanggal[i].toFixed(2) : "-";
            }
            
            realisasiSummaryData.push(dataRow);
        });
        
        return { realisasiSummaryData, realisasiDetailDataPerAktivitas };
    };

    // Fungsi untuk generate data chart (perbandingan Rencana vs Realisasi)
    const generateChartData = (rencanaData, realisasiData) => {
        const labels = aktivitasList;
        const rencanaValues = [];
        const realisasiValues = [];
        const selisihValues = [];
        const persentaseValues = [];
        
        aktivitasList.forEach((aktivitas) => {
            // Cari data rencana untuk aktivitas ini
            const rencanaItem = rencanaData.find(item => item.aktivitas === aktivitas);
            // Cari data realisasi untuk aktivitas ini
            const realisasiItem = realisasiData.find(item => item.aktivitas === aktivitas);
            
            // Parse nilai total - gunakan ensureNumber untuk menghindari null/undefined
            const rencanaValue = rencanaItem ? ensureNumber(rencanaItem.total) : 0;
            const realisasiValue = realisasiItem ? ensureNumber(realisasiItem.total) : 0;
            const selisih = realisasiValue - rencanaValue;
            const persentase = rencanaValue > 0 ? (realisasiValue / rencanaValue) * 100 : 0;
            
            // Simpan semua nilai termasuk yang 0
            rencanaValues.push(rencanaValue);
            realisasiValues.push(realisasiValue);
            selisihValues.push(selisih);
            persentaseValues.push(persentase);
        });
        
        return { 
            labels, 
            rencanaData: rencanaValues, 
            realisasiData: realisasiValues,
            selisihData: selisihValues,
            persentaseData: persentaseValues
        };
    };

    // Generate summary data, detail data, dan chart data
    useEffect(() => {
        if (selectedWeek && selectedYear && boomData.length > 0 && lokasiData.length > 0) {
            try {
                setLoading(true);
                
                // 1. Dapatkan tanggal-tanggal untuk week yang dipilih
                const weekDateRange = getWeekDateRange(selectedYear, selectedWeek);
                console.log(`Week ${selectedWeek} ${selectedYear} - Jumlah hari: ${weekDateRange.length}`);
                console.log("Tanggal dalam week:", weekDateRange.map(d => formatDate(d)));
                
                // 2. Generate data untuk tab Rencana
                const { summaryData, detailDataPerAktivitas } = generateRencanaData(
                    boomData, 
                    lokasiData, 
                    weekDateRange, 
                    selectedYear, 
                    selectedWeek
                );
                
                // 3. Generate data untuk tab Realisasi
                const { realisasiSummaryData, realisasiDetailDataPerAktivitas } = generateRealisasiData(
                    boomData,
                    lokasiData,
                    weekDateRange,
                    selectedYear,
                    selectedWeek
                );
                
                // 4. Generate data untuk chart
                const chartDataResult = generateChartData(summaryData, realisasiSummaryData);
                
                console.log("Final summary data (Rencana):", summaryData);
                console.log("Final summary data (Realisasi):", realisasiSummaryData);
                console.log("Final chart data:", chartDataResult);
                console.log("Final detail data count (Rencana):", detailDataPerAktivitas.length);
                console.log("Final detail data count (Realisasi):", realisasiDetailDataPerAktivitas.length);
                
                setDataList(summaryData);
                setRealisasiDataList(realisasiSummaryData);
                setDetailData(detailDataPerAktivitas);
                setRealisasiDetailData(realisasiDetailDataPerAktivitas);
                setChartData(chartDataResult);
                setError(null);
                
            } catch (err) {
                console.error("❌ Error generating summary data:", err);
                setError("Gagal memproses data summary boom sprayer");
            } finally {
                setLoading(false);
            }
        } else {
            console.log("Data tidak lengkap:", {
                selectedWeek, 
                selectedYear, 
                boomDataLength: boomData.length,
                lokasiDataLength: lokasiData.length
            });
            setDataList([]);
            setRealisasiDataList([]);
            setDetailData([]);
            setRealisasiDetailData([]);
            setChartData({ 
                labels: [], 
                rencanaData: [], 
                realisasiData: [],
                selisihData: [],
                persentaseData: []
            });
        }
    }, [selectedWeek, selectedYear, boomData, lokasiData]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleYearClick = (event) => {
        setYearAnchorEl(event.currentTarget);
    };

    const handleYearClose = () => {
        setYearAnchorEl(null);
    };

    const handleYearSelect = (year) => {
        setSelectedYear(year);
        handleYearClose();
        // Reset week saat tahun berubah
        setSelectedWeek(null);
        setWeekDates([]);
    };

    const handleWeekClick = (event) => {
        setWeekAnchorEl(event.currentTarget);
    };

    const handleWeekClose = () => {
        setWeekAnchorEl(null);
    };

    const handleWeekSelect = (week) => {
        setSelectedWeek(week);
        const dates = getWeekDateRange(selectedYear, week);
        setWeekDates(dates);
        handleWeekClose();
    };

    // Fungsi untuk membuka dialog detail berdasarkan tab aktif
    const handleOpenDetailDialog = () => {
        setDetailDialogOpen(true);
    };

    // Fungsi untuk menutup dialog detail
    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
    };

    // Fungsi untuk mendapatkan detail data berdasarkan tab aktif
    const getActiveDetailData = () => {
        if (activeTab === 1) { // Tab Rencana
            return detailData;
        } else if (activeTab === 2) { // Tab Realisasi
            return realisasiDetailData;
        }
        return [];
    };

    // Fungsi untuk mendapatkan judul dialog berdasarkan tab aktif
    const getDialogTitle = () => {
        if (activeTab === 1) {
            return `Detail Lokasi Rencana - Summary Boom Sprayer ${selectedYear} Week ${selectedWeek || '-'}`;
        } else if (activeTab === 2) {
            return `Detail Lokasi Realisasi - Summary Boom Sprayer ${selectedYear} Week ${selectedWeek || '-'}`;
        }
        return `Detail Lokasi - Summary Boom Sprayer ${selectedYear} Week ${selectedWeek || '-'}`;
    };

    // Fungsi untuk memeriksa apakah tombol Detail Lokasi harus di-disable
    const isDetailButtonDisabled = () => {
        return !selectedWeek || (activeTab !== 1 && activeTab !== 2);
    };

    const getTotalColumns = () => {
        // No + Aktivitas + Total + jumlah tanggal dalam week
        return 3 + weekDates.length;
    };

    // Fungsi untuk mendapatkan rentang tanggal yang ditampilkan
const getDateRangeText = () => {
        if (weekDates.length === 0) return "Pilih Week untuk melihat data";
        
        if (weekDates.length === 1) {
            return `Week ${selectedWeek} - ${formatDate(weekDates[0])}`;
        }
        
        return `Week ${selectedWeek} - ${weekDates.length} hari (${formatDate(weekDates[0])} sampai ${formatDate(weekDates[weekDates.length - 1])})`;
    };

    // Fungsi untuk mendapatkan data yang aktif berdasarkan tab
    const getActiveData = () => {
        switch(activeTab) {
            case 1: // Tab Rencana
                return dataList;
            case 2: // Tab Realisasi
                return realisasiDataList;
            default:
                return [];
        }
    };

    // Fungsi untuk mendapatkan judul berdasarkan tab aktif
    const getTitle = () => {
        switch(activeTab) {
            case 0: // Tab Bar Chart
                return `Perbandingan Rencana vs Realisasi - Week ${selectedWeek || '-'} ${selectedYear || ''}`;
            case 1: // Tab Rencana
                return `Rencana Boom Sprayer ${selectedYear || ''} Week ${selectedWeek || '-'}`;
            case 2: // Tab Realisasi
                return `Realisasi Boom Sprayer ${selectedYear || ''} Week ${selectedWeek || '-'}`;
            default:
                return `Summary Boom Sprayer ${selectedYear || ''}`;
        }
    };

    // Fungsi untuk menghitung sumbu Y maksimum dengan interval 10
const getYAxisConfig = () => {
    const { rencanaData, realisasiData } = chartData;
    if (rencanaData.length === 0 && realisasiData.length === 0) {
        return { min: 0, max: 100, tickInterval: 20 };
    }
    
    const maxRencana = Math.max(...rencanaData);
    const maxRealisasi = Math.max(...realisasiData);
    const maxValue = Math.max(maxRencana, maxRealisasi);
    
    // Maksimum = nilai terbesar + 20
    const finalMax = maxValue + 20;
    
    return { 
        min: 0, 
        max: finalMax, 
        tickInterval: 20 
    };
};

    // Fungsi untuk memendekkan label aktivitas jika terlalu panjang
const shortenLabel = (label) => {
    if (!label) return '';
    
    // Jika label sudah pendek, biarkan
    if (label.length <= 15) return label;
    
    // Coba ambil kata-kata penting
    const words = label.split(' ');
    
    // Jika hanya 1 kata, potong saja
    if (words.length === 1) {
        return label.substring(0, 12) + '...';
    }
    
    // Ambil inisial atau kata penting
    if (label.includes('(')) {
        // Jika ada tanda kurung, ambil bagian sebelum kurung
        const beforeBracket = label.split('(')[0].trim();
        if (beforeBracket.length <= 15) return beforeBracket;
    }
    
    // Ambil 2-3 kata pertama
    return words.slice(0, 2).join(' ') + '...';
};

    // Custom Tooltip untuk chart
// Custom Tooltip yang menampilkan teks dari aktivitasList
const CustomChartTooltip = ({ series, itemData, itemIndex }) => {
    if (itemIndex === null || itemIndex === undefined) return null;
    
    // Gunakan aktivitasList untuk mendapatkan teks berdasarkan indeks
    const aktivitas = aktivitasList[itemIndex] || `Aktivitas ${itemIndex + 1}`;
    const rencana = chartData.rencanaData[itemIndex] || 0;
    const realisasi = chartData.realisasiData[itemIndex] || 0;
    const selisih = chartData.selisihData[itemIndex] || 0;
    const persentase = chartData.persentaseData[itemIndex] || 0;
    
    return (
        <Paper sx={{ 
            p: 1.5, 
            bgcolor: 'background.paper',
            border: '1px solid #e0e0e0',
            boxShadow: 3,
            maxWidth: 250
        }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
                {aktivitas}
            </Typography>
            <Typography variant="caption" color="primary" display="block" gutterBottom>
                Rencana: <strong>{rencana.toFixed(2)} Ha</strong>
            </Typography>
            <Typography variant="caption" color="success.main" display="block" gutterBottom>
                Realisasi: <strong>{realisasi.toFixed(2)} Ha</strong>
            </Typography>
            <Typography 
                variant="caption" 
                color={selisih >= 0 ? "success.main" : "error.main"} 
                display="block"
                gutterBottom
            >
                Selisih: <strong>{selisih.toFixed(2)} Ha</strong>
                {selisih > 0 ? " (+)" : selisih < 0 ? " (-)" : ""}
            </Typography>
            <Typography 
                variant="caption" 
                color={persentase >= 100 ? "success.main" : persentase >= 80 ? "warning.main" : "error.main"} 
                display="block"
            >
                Persentase: <strong>{persentase.toFixed(1)}%</strong>
            </Typography>
        </Paper>
    );
};

    return (
        <Box>
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" color="text.primary">
                        {getTitle()}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Tahun Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleYearClick}
                            label={selectedYear ? selectedYear.toString() : "Pilih Tahun"}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon 
                                    sx={{ 
                                        transform: yearAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s'
                                    }} 
                                />
                            }
                            onDelete={handleYearClick}
                            sx={{ cursor: "pointer" }}
                        />
                        
                        {/* Week Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleWeekClick}
                            label={selectedWeek ? `Week ${selectedWeek}` : "Pilih Week"}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon 
                                    sx={{ 
                                        transform: weekAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s'
                                    }} 
                                />
                            }
                            onDelete={handleWeekClick}
                            sx={{ cursor: "pointer" }}
                        />
                        
                        {/* Chip untuk membuka detail lokasi - Dipindahkan ke sini */}
                        {activeTab !== 0 && (
                            <Chip
                                label={`Detail Lokasi ${activeTab === 1 ? 'Rencana' : activeTab === 2 ? 'Realisasi' : ''}`}
                                size="small"
                                onClick={handleOpenDetailDialog}
                                color="secondary"
                                variant="outlined"
                                sx={{ 
                                    cursor: isDetailButtonDisabled() ? "not-allowed" : "pointer",
                                    opacity: isDetailButtonDisabled() ? 0.5 : 1 
                                }}
                                disabled={isDetailButtonDisabled()}
                            />
                        )}
                    </Stack>
                </Stack>

                {/* Tabs - Sekarang ada 3 tab (Bar Chart, Rencana, dan Realisasi) */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": { display: "none" },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none",
                                "&:hover": { backgroundColor: "transparent" },
                            },
                        }}
                    >
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Bar Chart"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Rencana"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Realisasi"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 2 ? "primary" : "default"}
                                    variant={activeTab === 2 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                {/* Main Content */}
                <Box
                    sx={{
                        height: 350,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
{activeTab === 0 && (
    <Box sx={{ 
        height: "100%", 
        width: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
    }}>
        {!selectedWeek ? (
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%'
            }}>
                <Typography variant="body1" color="text.secondary">
                    Silakan pilih Week untuk menampilkan chart
                </Typography>
            </Box>
        ) : chartData.labels.length === 0 ? (
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%'
            }}>
                <Typography variant="body1" color="text.secondary">
                    {loading ? "Memuat data chart..." : `Tidak ada data untuk Week ${selectedWeek}`}
                </Typography>
            </Box>
        ) : (
            // lakukan rotasi pada teks xAxis 
<BarChart
    xAxis={[{
        data: [
            "1. Pre Planting",
            "2. Herb.post plant./emerg. mek.",
            "3. Herbisida booster Mekanis",
            "4. Pupuk spray mek.  <ppk>",
            "5. Forcing 1 (satu) mek.",
            "6. Forcing 2 (dua) mek.",
            "7. Insektisida I Setelah Forcing",
            "8. Ripening",
            "9. Pestisida Spray",
            "10. Siram Buah"
        ],
        scaleType: "band",
        label: "",
        tickLabelStyle: {
            angle: 45,
            textAnchor: 'start',
            fontSize: 8,
            fill: '#333',
            fontWeight: 'normal',
        },
    }]}
    yAxis={[
        {
            ...getYAxisConfig(),
            label: "",
            disableLine: false,
            disableTicks: false,
            tickLabelStyle: {
                fontSize: 10,
            },
        },
        
    ]}
    series={[
        {
            data: chartData.rencanaData,
            label: "Rencana (Ha)",
            color: "#ff9800",
        },
        {
            data: chartData.realisasiData,
            label: "Realisasi (Ha)",
            color: "#4caf50",
        },
        {
            data: chartData.selisihData.map(Math.abs), // Gunakan nilai absolut untuk selisih
            label: "Selisih (Ha)",
            color: "#607d8b",
        },
        {
            data: chartData.persentaseData,
            label: "Persentase (%)",
            color: "#9c27b0",
            yAxisKey: 'right-axis',
        },
    ]}
    margin={{ top: 20, right: 10, bottom: 0, left: 10 }} // Tambah margin bottom lebih besar
    slotProps={{
        legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "right" },
            padding: 0,
            itemMarkWidth: 8,
            itemMarkHeight: 8,
            labelStyle: { fontSize: 11 },
            itemGap: 8,
        },
        tooltip: {
            slot: CustomChartTooltip,
        },
    }}
    height={300}
    disableClipPath={true}
    sx={{
        width: '100%',
        "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
            stroke: "#666666",
        },
        "& .MuiChartsAxis-left .MuiChartsAxis-line": {
            stroke: "#666666",
        },
        "& .MuiChartsAxis-right .MuiChartsAxis-line": {
            stroke: "#666666",
        },
        "& .MuiChartsAxis-left .MuiChartsAxis-label": {
            display: "none",
        },
        "& .MuiChartsAxis-right .MuiChartsAxis-label": {
            display: "none",
        },
        // Hapus atau komentari semua styling untuk tickLabel di sini
        // Biarkan BarChart yang mengatur styling dari tickLabelStyle
    }}
/>
        )}
    </Box>
)}

                    {(activeTab === 1 || activeTab === 2) && (
                        <Box sx={{ 
                            height: "100%", 
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}>
                            <CustomTableContainer
                                sx={{
                                    flex: 1,
                                    border: "none",
                                    borderRadius: 1,
                                    width: "100%",
                                    minHeight: 0,
                                }}
                                hoverScrollbar={true}
                                scrollbarHeight="8px"
                                scrollbarWidth="8px"
                                scrollbarThumbColor="#c1c1c1"
                                scrollbarHoverColor="#a8a8a8"
                            >
                                <Table 
                                    stickyHeader 
                                    size="small"
                                    sx={{ tableLayout: 'fixed', width: '100%' }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell rowSpan={2} className="ded-tch" sx={{ width: 50, padding: "8px 4px", backgroundColor: "#f5f5f5" }} align="center">
                                                No
                                            </TableCell>
                                            <TableCell rowSpan={2} className="ded-tch" sx={{ width: 240, padding: "8px 4px", backgroundColor: "#f5f5f5" }}>
                                                Aktivitas
                                            </TableCell>
                                            <TableCell rowSpan={2} className="ded-tch" sx={{ width: 80, padding: "8px 4px", backgroundColor: "#f5f5f5" }} align="center">
                                                Total
                                            </TableCell>
                                            {weekDates.length > 0 ? (
                                                <TableCell colSpan={weekDates.length} className="ded-tch" sx={{ padding: "8px 4px", backgroundColor: "#f5f5f5" }} align="center">
                                                    {getDateRangeText()} 
                                                </TableCell>
                                            ) : (
                                                <TableCell className="ded-tch" sx={{ padding: "8px 4px", backgroundColor: "#f5f5f5" }} align="center">
                                                    Pilih Week untuk melihat data
                                                </TableCell>
                                            )}
                                        </TableRow>
                                        {weekDates.length > 0 && (
                                            <TableRow>
                                                {weekDates.map((date, index) => (
                                                    <TableCell key={`date-${index}`} className="ded-tc" sx={{ width: 80, padding: "8px 4px", backgroundColor: "#f5f5f5" }} align="center">
                                                        <div style={{ fontSize: "0.75rem" }}>{formatDate(date)}</div>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        )}
                                    </TableHead>

                                    <TableBody>
                                        {!selectedWeek ? (
                                            <TableRow>
                                                <TableCell colSpan={getTotalColumns()} align="center" sx={{ padding: "40px 16px", height: "200px" }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Silakan pilih Week untuk menampilkan data
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : getActiveData().length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={getTotalColumns()} align="center" sx={{ padding: "40px 16px", height: "200px" }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {loading ? "Memuat data..." : `Tidak ada data ${activeTab === 1 ? 'rencana' : 'realisasi'} untuk Week ${selectedWeek}`}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            getActiveData().map((item) => (
                                                <TableRow key={item.no} hover>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 4px" }} align="center">
                                                        {item.no}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 4px" }}>
                                                        {item.aktivitas}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 4px" }} align="center">
                                                        {item.total}
                                                    </TableCell>
                                                    {weekDates.map((_, index) => (
                                                        <TableCell key={`col-${index}`} className="ded-tbtrtc" sx={{ padding: "8px 4px" }} align="center">
                                                            {item[`day_${index}`] || "-"}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </CustomTableContainer>
                        </Box>
                    )}
                </Box>

                {/* Menus untuk dropdown */}
                <Menu anchorEl={yearAnchorEl} open={Boolean(yearAnchorEl)} onClose={handleYearClose}>
                    {yearList.map((year) => (
                        <MenuItem key={year} onClick={() => handleYearSelect(year)} selected={selectedYear === year}>
                            {year}
                        </MenuItem>
                    ))}
                </Menu>

                <Menu anchorEl={weekAnchorEl} open={Boolean(weekAnchorEl)} onClose={handleWeekClose}>
                    {weekList.map((week) => (
                        <MenuItem key={week} onClick={() => handleWeekSelect(week)} selected={selectedWeek === week}>
                            Week {week}
                        </MenuItem>
                    ))}
                </Menu>
            </Paper>

            {/* Dialog untuk Detail Lokasi - Full Screen Slide In */}
            <Dialog
                fullScreen
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                TransitionComponent={Transition}
            >
                <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                    {/* Header Dialog */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper'
                    }}>
                        <Typography variant="h6" component="div">
                            {getDialogTitle()}
                        </Typography>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={handleCloseDetailDialog}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content Dialog */}
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <DetailSummaryBoom
                            detailData={getActiveDetailData()}
                            loading={loading}
                            selectedWeek={selectedWeek}
                            isRealisasi={activeTab === 2} // Tambahkan prop untuk membedakan Rencana/Realisasi
                        />
                    </Box>
                </Box>
            </Dialog>

        </Box>
    );
}

export default SummaryBoom;